import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { FeaturesTable } from '@/features/features/components/FeaturesTable';
import { useFeaturesPageViewModel } from '@/features/features/hooks/useFeaturesPageViewModel';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { s } from '@/theme/tokens';
import { DelayedSkeletonGate, ListPageSkeletonPreset } from '@/ui/composites';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Box, Text } from '@/ui/primitives';
import { PageShell } from '@/ui/primitives/PageShell';
import type { FeatureStatus } from '@flowqa/shared';
import type { ReactElement } from 'react';

export const FeaturesPage = (): ReactElement => {
  const {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    tagOptions,
    tagOptionsLoading,
    onStatusToggle,
    onTagsChange,
    tableFilters,
    featureMetricCards,
    dateFiltersEnabled,
    showPageSkeleton,
  } = useFeaturesPageViewModel();

  const skeleton = (
    <PageShell maxWidth="xl" paddingX="lg" paddingY="xl" paddingTop="xl" paddingBottom="lg">
      <ListPageSkeletonPreset variant="features" showDateRange={dateFiltersEnabled} />
    </PageShell>
  );

  return (
    <DelayedSkeletonGate show={showPageSkeleton} fallback={skeleton}>
      <ListPageTemplate
        headerActionsMode="separate"
        title={
          <Text role="sectionTitle" component="h1">
            Features
          </Text>
        }
        headerActions={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap="md"
            wrap="wrap"
            sx={{
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              width: '100%',
            }}
          >
            <TagsFilterField
              value={selectedTags}
              options={tagOptions}
              onChange={onTagsChange}
              emptyLabel={tagOptionsLoading ? 'Loading Tags...' : 'All Tags'}
            />
            <DashboardDateRangeControls value={range} onChange={setRange} fullWidthOnMobile />
          </Stack>
        }
        summary={
          <Box sx={{ mt: s('sm') }}>
            <StatusMetricCardsRow
              cards={featureMetricCards}
              selectedIds={selectedStatuses}
              onToggle={(status) => {
                if (status === 'total') return;
                onStatusToggle(status as FeatureStatus);
              }}
            />
          </Box>
        }
        content={
          <FeaturesTable
            persistKey="features-table"
            fixedFilters={tableFilters}
            pageSizeOptions={[5]}
            initialPageSize={5}
            disablePageSizeSelect
          />
        }
      />
    </DelayedSkeletonGate>
  );
};
